import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, CheckCircle2, UserPlus, Zap } from 'lucide-react';
import { EventProLogo } from '../components/ui/Logo';
import { API_URL } from '../lib/utils';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const { register, isLoading } = useAuthStore();
  
  const [companyName, setCompanyName] = useState("Chargement...");
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/settings/public-config`)
        .then(res => res.json())
        .then(data => {
            setCompanyName(data.company_name);
            setCompanyLogo(data.logo_base64);
        })
        .catch(() => setCompanyName(""));
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      const response = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
      });

      if (response?.is_active) {
        toast.success('Compte créé et activé !');
        navigate('/dashboard');
      } else {
        setIsRegistered(true);
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création du compte');
    }
  };

  if (isRegistered) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-100">
        <Card className="w-full max-w-md border-none shadow-2xl animate-in zoom-in-95 duration-500 bg-white">
          <CardContent className="pt-10 pb-8 text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-100 shadow-inner">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
            </div>
            <div className="space-y-2">
              <CardTitle className="text-3xl font-bold tracking-tight text-slate-900">Inscription reçue !</CardTitle>
              <CardDescription className="text-base text-slate-600">
                Votre compte <strong>{formData.email}</strong> a bien été créé.
              </CardDescription>
            </div>
            <div className="bg-amber-50/50 p-5 rounded-xl text-sm text-slate-700 text-left border border-amber-100/50 leading-relaxed shadow-sm">
              <p className="font-bold text-amber-800 mb-2">Dernière étape de sécurité :</p>
              Un administrateur doit valider votre accès avant que vous puissiez vous connecter. 
              Vous recevrez un e-mail de confirmation dès que votre compte sera activé.
            </div>
            <Button className="w-full h-12 text-lg shadow-md" onClick={() => navigate('/login')}>
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    // "relative" permet de positionner le logo EventPro par-dessus le fond
    <div className="relative min-h-screen flex flex-col items-center justify-center p-4 bg-slate-100 font-sans">
      
      {/* LOGO EVENTPRO - EN HAUT À GAUCHE */}
      <div className="absolute top-6 left-6 md:top-8 md:left-8">
        <div className="bg-sky-500/50 backdrop-blur px-4 py-2 rounded-xl shadow-sm flex items-center justify-center border border-slate-200">
            <EventProLogo className="scale-90 origin-left" />
        </div>
      </div>

      <div className="w-full max-w-md space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 mt-16 md:mt-0">
        
        {/* LOGO DU CLIENT - CENTRÉ */}
        <div className="flex flex-col items-center justify-center mb-6">
            {companyLogo ? (
                // L'astuce mix-blend-multiply permet de rendre transparent le fond blanc/gris d'un JPEG
                <img src={companyLogo} alt="Logo Client" className="h-20 w-auto object-contain mix-blend-multiply drop-shadow-sm mb-2" />
            ) : (
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-slate-200 mb-2 text-primary">
                    <Zap className="w-8 h-8" />
                </div>
            )}
            
            {companyName !== "Chargement..." && (
                <h1 className="text-2xl font-bold text-slate-900">{companyName}</h1>
            )}
            <p className="text-slate-500 font-bold tracking-widest uppercase text-xs mt-2">Espace Collaborateur</p>
        </div>

        {/* FORMULAIRE */}
        <Card className="border-none shadow-xl bg-white rounded-2xl overflow-hidden">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-slate-700 font-medium">Nom complet</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  placeholder="Ex: Jean Dupont"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="bg-slate-50 focus:bg-white border-slate-200 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">Email professionnel</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="vous@entreprise.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-slate-50 focus:bg-white border-slate-200 h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pr-10 bg-slate-50 focus:bg-white border-slate-200 h-12"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-12 px-3 text-slate-400 hover:text-slate-600 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="bg-slate-50 focus:bg-white border-slate-200 h-12"
                />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    "Rejoindre l'équipe"
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-8 text-center border-t border-slate-100 pt-6">
              <p className="text-sm text-slate-500">
                Vous avez déjà un compte ?{' '}
                <Link to="/login" className="text-primary hover:underline font-bold transition-colors">
                  Connectez-vous ici
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;